if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.scheduleAddonWarmup();
      }

      scheduleAddonWarmup() {
        const addonSelects = this.querySelectorAll('.product-addon-select');
        if (!addonSelects.length) return;

        const warm = () => this.primeAddonSelections().catch(() => {});
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(warm, { timeout: 1200 });
        } else {
          window.setTimeout(warm, 250);
        }
      }

      async fetchAccessoriesProductsForWarmup() {
        this._addonVariantCache = this._addonVariantCache || {};
        const key = 'collection:accessories';
        if (this._addonVariantCache[key]) return this._addonVariantCache[key];

        const response = await fetch('/collections/accessories/products.json?limit=250', {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
          }
        });
        if (!response.ok) return [];

        const data = await response.json();
        const products = Array.isArray(data?.products) ? data.products : [];
        this._addonVariantCache[key] = products;
        return products;
      }

      normalizeAddonText(value) {
        return (value || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
      }

      handleizeAddonText(value) {
        return (value || '')
          .toString()
          .trim()
          .toLowerCase()
          .replace(/&/g, ' and ')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      resolveAddonVariantFromProducts(products, select, option) {
        if (!Array.isArray(products) || !products.length || !option) return null;

        const skuHint = (option.dataset.addonSku || select?.dataset?.addonSku || '').toString().trim().toLowerCase();
        if (skuHint) {
          for (const product of products) {
            const variants = Array.isArray(product?.variants) ? product.variants : [];
            const exactVariant = variants.find((variant) => (variant?.sku || '').toString().trim().toLowerCase() === skuHint);
            if (exactVariant?.id) return exactVariant.id;
          }
        }

        const optionValue = (option.value || '').toString().trim().toLowerCase();
        const optionText = (option.textContent || '').replace(/\s*\$[\d,]+(?:\.\d{2})?.*$/g, '').trim();
        const addonType = (option.dataset.addonType || select?.dataset?.addonType || '').toString().trim().toLowerCase();

        const rawHandleHints = [
          option.dataset.addonFallbackHandle,
          select?.dataset?.addonFallbackHandle,
          ...(option.dataset.addonFallbackHandles || '').split(','),
          ...(select?.dataset?.addonFallbackHandles || '').split(','),
          this.handleizeAddonText(optionText),
          optionValue,
          addonType
        ];

        const handleHints = [...new Set(rawHandleHints.map((value) => (value || '').toString().trim().toLowerCase()).filter(Boolean))];
        for (const handleHint of handleHints) {
          const matchingProduct = products.find((product) => ((product?.handle || '').toString().trim().toLowerCase()) === handleHint);
          if (matchingProduct?.variants?.[0]?.id) return matchingProduct.variants[0].id;
        }

        const normalizedOptionText = this.normalizeAddonText(optionText);
        const matchesProduct = (product) => {
          const productTitle = this.normalizeAddonText(product?.title || '');
          if (!productTitle) return false;
          if (normalizedOptionText && (productTitle === normalizedOptionText || productTitle.includes(normalizedOptionText) || normalizedOptionText.includes(productTitle))) return true;
          if (addonType === 'tpms') return productTitle.includes('tpms') || productTitle.includes('tire pressure') || productTitle.includes('sensor');
          if (addonType === 'lugkit') {
            const wantsSpline = optionValue.includes('spline') || normalizedOptionText.includes('spline');
            const wantsSpike = optionValue.includes('spike') || normalizedOptionText.includes('spike');
            const wantsChrome = optionValue.includes('chrome') || normalizedOptionText.includes('chrome');
            const wantsBlack = optionValue.includes('black') || normalizedOptionText.includes('black');
            return productTitle.includes('lug') && (!wantsSpline || productTitle.includes('spline')) && (!wantsSpike || productTitle.includes('spike')) && (!wantsChrome || productTitle.includes('chrome')) && (!wantsBlack || productTitle.includes('black'));
          }
          if (addonType === 'lugwrench') {
            const wantsGorilla = optionValue.includes('gorilla') || normalizedOptionText.includes('gorilla');
            return wantsGorilla ? productTitle.includes('gorilla') || productTitle.includes('tw705') : productTitle.includes('lug wrench');
          }
          return false;
        };

        const matchedProduct = products.find(matchesProduct);
        return matchedProduct?.variants?.[0]?.id || null;
      }

      async primeAddonSelections() {
        const addonSelects = Array.from(this.querySelectorAll('.product-addon-select'));
        if (!addonSelects.length) return;

        const products = await this.fetchAccessoriesProductsForWarmup();
        if (!products.length) return;

        addonSelects.forEach((select) => {
          Array.from(select.options || []).forEach((option) => {
            if (!option || option.disabled || !option.value) return;
            const existingVariantId = option.dataset.addonVariantId || option.dataset.variantId || '';
            if (/^\d+$/.test(String(existingVariantId))) return;

            const resolvedVariantId = this.resolveAddonVariantFromProducts(products, select, option);
            if (resolvedVariantId) {
              option.dataset.addonVariantId = resolvedVariantId;
              option.dataset.variantId = resolvedVariantId;
            }
          });
        });
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        const productVariantIdForEvents = formData.get('id');

        let useJsonAdd = false;
        let jsonPayload = null;

        // Duallys Only – add selected add-ons (TPMS / Lug Kit) as additional cart line items
        try {
          const addonSelects = Array.from(this.querySelectorAll('.product-addon-select'));
          if (addonSelects.length) {
            const addonItems = [];

	            // Cache resolved lookups so we don't repeatedly fetch product JSON.
	            this._addonVariantCache = this._addonVariantCache || {};

	            const resolveVariantFromProductJSON = (productJson, skuHint, allowFallback = true) => {
	              if (!productJson || !Array.isArray(productJson.variants) || !productJson.variants.length) return null;
	              if (skuHint) {
	                const wantedSku = skuHint.toString().trim().toLowerCase();
	                const exactVariant = productJson.variants.find((vv) => (vv?.sku || '').toString().trim().toLowerCase() === wantedSku);
	                if (exactVariant && exactVariant.id) return exactVariant.id;
	                if (!allowFallback) return null;
	              }
	              return productJson.variants[0].id || null;
	            };
	            const fetchProductJSON = async (handle) => {
	              if (!handle) return null;
	              const key = `handle:${handle}`;
	              if (this._addonVariantCache[key]) return this._addonVariantCache[key];
	              try {
	                const res = await fetch(`/products/${handle}.js`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
	                if (!res.ok) return null;
	                const json = await res.json();
	                this._addonVariantCache[key] = json;
	                return json;
	              } catch (e) {
	                return null;
	              }
	            };

	            const suggestProductHandles = async (query) => {
	              const q = (query || '').toString().trim();
	              if (!q) return [];
	              const key = `suggest:${q.toLowerCase()}`;
	              if (this._addonVariantCache[key]) return this._addonVariantCache[key];
	              try {
	                const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants,tag`;
	                const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
	                if (!res.ok) return [];
	                const data = await res.json();
	                const products = data?.resources?.results?.products || [];
	                const handles = products.map((product) => product?.handle).filter(Boolean);
	                this._addonVariantCache[key] = handles;
	                return handles;
	              } catch (e) {
	                return [];
	              }
	            };
            const normalizeTitle = (value) => (value || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
            const handleizeValue = (value) => (value || '')
              .toString()
              .trim()
              .toLowerCase()
              .replace(/&/g, ' and ')
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '');

            const fetchAccessoriesProducts = async () => {
              const key = 'collection:accessories';
              if (this._addonVariantCache[key]) return this._addonVariantCache[key];
              try {
                const res = await fetch('/collections/accessories/products.json?limit=250', {
                  headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json'
                  }
                });
                if (!res.ok) return [];
                const data = await res.json();
                const products = Array.isArray(data?.products) ? data.products : [];
                this._addonVariantCache[key] = products;
                return products;
              } catch (e) {
                return [];
              }
            };

            const resolveVariantFromAccessoriesCollection = async ({ skuHint, handleHints = [], queryCandidates = [], optionText = '', addonType = '', optionValue = '' }) => {
              const products = await fetchAccessoriesProducts();
              if (!Array.isArray(products) || !products.length) return null;

              const normalizedSku = (skuHint || '').toString().trim().toLowerCase();
              if (normalizedSku) {
                for (const product of products) {
                  const variants = Array.isArray(product?.variants) ? product.variants : [];
                  const exactVariant = variants.find((variant) => (variant?.sku || '').toString().trim().toLowerCase() === normalizedSku);
                  if (exactVariant?.id) return exactVariant.id;
                }
              }

              const titleHints = [optionText, ...queryCandidates].map(normalizeTitle).filter(Boolean);
              const handleCandidates = [
                ...handleHints,
                handleizeValue(optionText),
                ...queryCandidates.map(handleizeValue),
                addonType,
                optionValue
              ].map((value) => (value || '').toString().trim().toLowerCase().replace(/^\/+|\/+$/g, '')).filter(Boolean);
              const uniqueHandleCandidates = [...new Set(handleCandidates)];

              const titleIncludesAny = (product) => {
                const productTitle = normalizeTitle(product?.title || '');
                if (!productTitle) return false;
                return titleHints.some((hint) => {
                  if (!hint) return false;
                  if (productTitle === hint) return true;
                  if (productTitle.includes(hint) || hint.includes(productTitle)) return true;
                  const hintTokens = hint.split(' ').filter(Boolean);
                  return hintTokens.length > 1 && hintTokens.every((token) => productTitle.includes(token));
                });
              };

              for (const handleCandidate of uniqueHandleCandidates) {
                const matchingProduct = products.find((product) => ((product?.handle || '').toString().trim().toLowerCase()) === handleCandidate);
                if (matchingProduct?.variants?.[0]?.id) return matchingProduct.variants[0].id;
              }

              let filteredProducts = products.filter((product) => titleIncludesAny(product));

              if (!filteredProducts.length && addonType === 'tpms') {
                filteredProducts = products.filter((product) => {
                  const productTitle = normalizeTitle(product?.title || '');
                  return productTitle.includes('tpms') || productTitle.includes('tire pressure');
                });
              }

              if (!filteredProducts.length && addonType === 'lugkit') {
                const normalizedOptionText = normalizeTitle(optionText);
                filteredProducts = products.filter((product) => {
                  const productTitle = normalizeTitle(product?.title || '');
                  if (!productTitle) return false;
                  const hasLugKeyword = productTitle.includes('lug');
                  const hasSplineKeyword = normalizedOptionText.includes('spline') ? productTitle.includes('spline') : true;
                  const hasSpikeKeyword = normalizedOptionText.includes('spike') ? productTitle.includes('spike') : true;
                  const hasChromeKeyword = normalizedOptionText.includes('chrome') ? productTitle.includes('chrome') : true;
                  const hasBlackKeyword = normalizedOptionText.includes('black') ? productTitle.includes('black') : true;
                  return hasLugKeyword && hasSplineKeyword && hasSpikeKeyword && hasChromeKeyword && hasBlackKeyword;
                });
              }

              const exactTitleMatch = filteredProducts.find((product) => normalizeTitle(product?.title || '') === normalizeTitle(optionText));
              if (exactTitleMatch?.variants?.[0]?.id) return exactTitleMatch.variants[0].id;

              const prioritizedProduct = filteredProducts.find((product) => {
                const productTitle = normalizeTitle(product?.title || '');
                if (addonType === 'tpms') return productTitle.includes('tpms') || productTitle.includes('sensor');
                if (addonType === 'lugkit') return productTitle.includes('lug');
                return true;
              }) || filteredProducts[0];

              if (prioritizedProduct?.variants?.[0]?.id) return prioritizedProduct.variants[0].id;
              return null;
            };
	            const resolveAddonVariantId = async (select, opt) => {
	              let variantId = opt?.dataset?.addonVariantId || opt?.dataset?.variantId || null;
	              if (variantId && /^\d+$/.test(String(variantId))) return parseInt(variantId, 10);

	              const normalizeHandle = (value) => (value || '').toString().trim().toLowerCase().replace(/^\/+|\/+$/g, '');
	              const optionValue = (opt?.value || '').toString().trim().toLowerCase();
	              const addonType = (opt?.dataset?.addonType || select?.dataset?.addonType || '').toString().trim().toLowerCase();
	              const optionText = (opt?.textContent || '').replace(/\s*\$[\d,]+(?:\.\d{2})?.*$/g, '').trim();

	              const builtInFallbacks = {
	                tpms: {
	                  handles: ['tpms', 'tpms-sensor', 'tpms-sensors', 'tire-pressure-monitoring-system', 'tire-pressure-monitoring-system-sensors'],
	                  queries: ['TPMS', 'TPMS Sensors', 'Tire Pressure Monitoring System']
	                },
	                'lugkit-chrome-spline': {
	                  handles: ['chrome-spline-lugs', 'chrome-spline-lug-kit', 'chrome-spline-lug-kits', 'chrome-spline-lug-nuts'],
	                  queries: ['Chrome Spline Lugs', 'Chrome Spline Lug Kit']
	                },
	                'lugkit-black-spline': {
	                  handles: ['black-spline-lugs', 'black-spline-lug-kit', 'black-spline-lug-kits', 'black-spline-lug-nuts'],
	                  queries: ['Black Spline Lugs', 'Black Spline Lug Kit']
	                },
	                'lugkit-chrome-spike': {
	                  handles: ['chrome-spike-lugs', 'chrome-spike-lug-kit', 'chrome-spike-lug-kits', 'chrome-spike-lug-nuts'],
	                  queries: ['Chrome Spike Lugs', 'Chrome Spike Lug Kit']
	                },
	                'lugkit-black-spike': {
	                  handles: ['black-spike-lugs', 'black-spike-lug-kit', 'black-spike-lug-kits', 'black-spike-lug-nuts'],
	                  queries: ['Black Spike Lugs', 'Black Spike Lug Kit']
	                }
	              };

	              const builtInFallback = builtInFallbacks[optionValue] || builtInFallbacks[addonType] || null;
	              const handleHint = opt?.dataset?.addonFallbackHandle || select?.dataset?.addonFallbackHandle || null;
	              const handleHintsRaw = opt?.dataset?.addonFallbackHandles || select?.dataset?.addonFallbackHandles || '';
	              const extraHandleHints = handleHintsRaw
	                ? handleHintsRaw.split(',').map((value) => normalizeHandle(value)).filter(Boolean)
	                : [];
	              const queryHint = opt?.dataset?.addonFallbackQuery || select?.dataset?.addonFallbackQuery || null;
	              const skuHint = opt?.dataset?.addonSku || select?.dataset?.addonSku || null;

	              const tryResolveFromHandle = async (handle, allowFallback = false) => {
	                if (!handle) return null;
	                const productJson = await fetchProductJSON(handle);
	                const resolved = resolveVariantFromProductJSON(productJson, skuHint, allowFallback);
	                if (resolved && /^\d+$/.test(String(resolved))) return parseInt(resolved, 10);
	                return null;
	              };

	              const hintedHandles = [normalizeHandle(handleHint), ...extraHandleHints, ...((builtInFallback?.handles || []).map(normalizeHandle))].filter(Boolean);
	              const uniqueHintedHandles = [...new Set(hintedHandles)];
	              for (const hintedHandle of uniqueHintedHandles) {
	                const resolvedFromHandle = await tryResolveFromHandle(hintedHandle, !skuHint);
	                if (resolvedFromHandle) return resolvedFromHandle;
	              }

              const queryCandidates = [queryHint, skuHint, optionText, ...(builtInFallback?.queries || [])].filter(Boolean);
              const candidateHandles = [];
              for (const query of queryCandidates) {
                candidateHandles.push(...await suggestProductHandles(query));
              }

              const seenHandles = new Set();
              for (const handle of candidateHandles) {
                const normalizedHandle = normalizeHandle(handle);
                if (!normalizedHandle || seenHandles.has(normalizedHandle) || uniqueHintedHandles.includes(normalizedHandle)) continue;
                seenHandles.add(normalizedHandle);
                const resolved = await tryResolveFromHandle(normalizedHandle, !skuHint);
                if (resolved) return resolved;
              }

              const resolvedFromAccessories = await resolveVariantFromAccessoriesCollection({
                skuHint,
                handleHints: uniqueHintedHandles,
                queryCandidates,
                optionText,
                addonType,
                optionValue
              });
              if (resolvedFromAccessories) return resolvedFromAccessories;

              if (skuHint) {
                for (const hintedHandle of uniqueHintedHandles) {
                  const resolvedWithFallback = await tryResolveFromHandle(hintedHandle, true);
                  if (resolvedWithFallback) return resolvedWithFallback;
                }
                for (const handle of candidateHandles) {
                  const normalizedHandle = normalizeHandle(handle);
                  if (!normalizedHandle) continue;
                  const resolvedWithFallback = await tryResolveFromHandle(normalizedHandle, true);
                  if (resolvedWithFallback) return resolvedWithFallback;
                }
              }
              return null;
	            };

	            for (const select of addonSelects) {
	              if (!select || !select.options || select.selectedIndex < 0) continue;
	              const opt = select.options[select.selectedIndex];
	              if (!opt) continue;

	              // Skip placeholder option (e.g., "Select an option")
	              if (opt.disabled || opt.value === '' || opt.value === null || opt.value === undefined) continue;

	              let variantId = opt.dataset.addonVariantId || opt.dataset.variantId || null;
	              const qtyStr = opt.dataset.addonQuantity || opt.dataset.qty;
	              const qty = qtyStr ? parseInt(qtyStr, 10) : 1;

	              if (variantId && /^\d+$/.test(String(variantId))) {
	                variantId = parseInt(variantId, 10);
	              } else {
	                variantId = await resolveAddonVariantId(select, opt);
	              }

	              if (variantId && qty && qty > 0) {
	                addonItems.push({ id: variantId, quantity: qty });
	              }
	            }


            if (addonItems.length) {
              const mainId = formData.get('id');
              const mainQtyRaw = formData.get('quantity');
              const mainQty = mainQtyRaw ? parseInt(mainQtyRaw, 10) : 1;
              const sellingPlan = formData.get('selling_plan');

              const mainProperties = {};
              for (const [key, value] of formData.entries()) {
                const match = key.match(/^properties\[(.+)\]$/);
                if (match && value !== '' && value !== null && value !== undefined) {
                  mainProperties[match[1]] = value;
                }
              }

              const items = [
                {
                  id: mainId,
                  quantity: Number.isNaN(mainQty) ? 1 : mainQty,
                },
              ];
              if (sellingPlan) items[0].selling_plan = sellingPlan;
              if (Object.keys(mainProperties).length) items[0].properties = mainProperties;

              addonItems.forEach((it) => items.push({ id: it.id, quantity: it.quantity }));

              useJsonAdd = true;
              jsonPayload = { items };
            }
          }
        } catch (e) {
          // Fail safe: if addon parsing fails, fall back to standard single-item add-to-cart
          console.error(e);
        }

        if (this.cart) {
          const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
          if (useJsonAdd && jsonPayload) {
            jsonPayload.sections = sectionIds;
            jsonPayload.sections_url = window.location.pathname;
          } else {
            formData.append('sections', sectionIds);
            formData.append('sections_url', window.location.pathname);
          }
          this.cart.setActiveElement(document.activeElement);
        }

        if (useJsonAdd && jsonPayload) {
          config.headers['Content-Type'] = 'application/json';
	          // Ensure the response is JSON when we post JSON.
	          config.headers['Accept'] = 'application/json';
          config.body = JSON.stringify(jsonPayload);
        } else {
          delete config.headers['Content-Type'];
          config.body = formData;
        }

	        // Shopify multi-item add is consistently supported via the `.js` endpoint.
	        const cartAddEndpoint = useJsonAdd && jsonPayload ? `${routes.cart_add_url}.js` : `${routes.cart_add_url}`;
	        const renderCartWithSections = (baseState) => {
	          if (!this.cart) return;
	          const sectionIds = this.cart.getSectionsToRender ? this.cart.getSectionsToRender().map((section) => section.id) : [];
	          if (!sectionIds.length) {
	            this.cart.renderContents(baseState);
	            return;
	          }
	          fetch(`${routes.cart_url}?sections=${sectionIds.join(',')}`, {
	            headers: {
	              'X-Requested-With': 'XMLHttpRequest',
	              'Accept': 'application/json'
	            }
	          })
	            .then((r) => r.json())
	            .then((sections) => {
	              const nextState = Object.assign({}, baseState, { sections });
	              this.cart.renderContents(nextState);
	            });
	        };


	        fetch(cartAddEndpoint, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
				productVariantId: productVariantIdForEvents,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
					productVariantId: productVariantIdForEvents,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      if (useJsonAdd && jsonPayload && !response.sections) { renderCartWithSections(response); } else { this.cart.renderContents(response); }
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                if (useJsonAdd && jsonPayload && !response.sections) { renderCartWithSections(response); } else { this.cart.renderContents(response); }
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
